import { useMemo } from 'react';
import {
    Calendar,
    TrendingUp,
    DollarSign,
    Package,
    BarChart3,
    ArrowUpRight,
    ArrowDownRight,
    Layers,
    Award,
    AlertTriangle,
    Grid3X3
} from 'lucide-react';
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    LineChart,
    Line,
    AreaChart,
    Area,
} from 'recharts';


import ChartCard from '../components/common/ChartCard';
import KPICard from '../components/common/KPICard';
import Heatmap from '../components/common/Heatmap';
import { useFilters } from '../context/FilterContext';
import { categories } from '../utils/mockData/categories';
import { applyMultiLevelFilters } from '../utils/filters';
import { formatCurrency, formatNumber, formatPercentage } from '../utils/formatters';
import { calculateGrowth } from '../utils/calculations';
import styles from './AnalysisCompact.module.css';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function WeeklyAnalysis() {
    const { appliedFilters, orders } = useFilters();

    // Get last 4 weeks
    const weekLabels = useMemo(() => {
        const weeks = [];
        const now = new Date();
        for (let i = 3; i >= 0; i--) {
            const weekStart = new Date(now);
            weekStart.setDate(now.getDate() - (i * 7) - now.getDay());
            const weekEnd = new Date(weekStart);
            weekEnd.setDate(weekStart.getDate() + 6);
            weeks.push({
                key: `W${4 - i}`,
                label: `Week ${4 - i}`,
                startDate: weekStart.toISOString().split('T')[0],
                endDate: weekEnd.toISOString().split('T')[0],
                short: `W${4 - i}`,
            });
        }
        return weeks;
    }, []);

    // Filter orders
    const filteredOrders = useMemo(() => {
        const filters = {
            cities: appliedFilters.cities,
            stores: appliedFilters.stores,
            pincodes: appliedFilters.pincodes,
        };
        return applyMultiLevelFilters(orders, filters);
    }, [appliedFilters]);

    // Aggregate by week
    const weeklyData = useMemo(() => {
        const weekMap = {};

        weekLabels.forEach(w => {
            weekMap[w.key] = {
                ...w,
                value: 0,
                volume: 0,
                orders: 0,
                categories: {},
                dailyBreakdown: Array(7).fill(null).map(() => ({ value: 0, volume: 0 })),
            };
        });

        filteredOrders.forEach(order => {
            const orderDate = new Date(order.date);
            const dayOfWeek = orderDate.getDay();

            // Find which week this order belongs to
            weekLabels.forEach(w => {
                if (order.date >= w.startDate && order.date <= w.endDate) {
                    weekMap[w.key].value += order.total;
                    weekMap[w.key].volume += order.itemCount;
                    weekMap[w.key].orders += 1;
                    weekMap[w.key].dailyBreakdown[dayOfWeek].value += order.total;
                    weekMap[w.key].dailyBreakdown[dayOfWeek].volume += order.itemCount;

                    order.items.forEach(item => {
                        if (!weekMap[w.key].categories[item.categoryId]) {
                            weekMap[w.key].categories[item.categoryId] = { value: 0, volume: 0 };
                        }
                        weekMap[w.key].categories[item.categoryId].value += item.total;
                        weekMap[w.key].categories[item.categoryId].volume += item.quantity;
                    });
                }
            });
        });

        return Object.values(weekMap);
    }, [filteredOrders, weekLabels]);

    // Week comparison cards
    const weekCards = useMemo(() => {
        return weeklyData.map((w, idx) => {
            const prev = idx > 0 ? weeklyData[idx - 1] : null;
            return {
                ...w,
                valueGrowth: prev ? calculateGrowth(w.value, prev.value) : 0,
                volumeGrowth: prev ? calculateGrowth(w.volume, prev.volume) : 0,
            };
        });
    }, [weeklyData]);

    // Best and worst day
    const { bestDay, worstDay } = useMemo(() => {
        const dayTotals = DAYS.map((day, idx) => ({
            day,
            value: weeklyData.reduce((sum, w) => sum + w.dailyBreakdown[idx].value, 0),
        }));

        const sorted = [...dayTotals].sort((a, b) => b.value - a.value);
        return { bestDay: sorted[0], worstDay: sorted[sorted.length - 1] };
    }, [weeklyData]);

    // Chart data: Weekly value
    const valueData = weeklyData.map(w => ({ week: w.short, Value: Math.round(w.value) }));

    // Chart data: Weekly volume
    const volumeData = weeklyData.map(w => ({ week: w.short, Volume: w.volume }));

    // Chart data: Daily breakdown stacked area
    const dailyBreakdownData = useMemo(() => {
        return DAYS.map((day, dayIdx) => {
            const dataPoint = { day };
            weeklyData.forEach(w => {
                dataPoint[w.short] = Math.round(w.dailyBreakdown[dayIdx].value);
            });
            return dataPoint;
        });
    }, [weeklyData]);

    // Heatmap data
    const heatmapData = useMemo(() => {
        return weeklyData.map(w =>
            w.dailyBreakdown.map(d => ({ value: d.value / 1000 })) // Scale down for display
        );
    }, [weeklyData]);

    // Category weekly revenue
    const categoryWeeklyData = useMemo(() => {
        return weeklyData.map(w => {
            const dataPoint = { week: w.short };
            categories.forEach(cat => {
                dataPoint[cat.shortName] = Math.round(w.categories[cat.id]?.value || 0);
            });
            return dataPoint;
        });
    }, [weeklyData]);

    // WoW Growth
    const wowGrowthData = weekCards.map(w => ({
        week: w.short,
        'Value Growth': w.valueGrowth,
        'Volume Growth': w.volumeGrowth,
    }));

    return (
        <div className={styles.page}>
            <header className={styles.header}>
                <h1 className={styles.title}>Weekly Analysis</h1>
                <p className={styles.subtitle}>Track performance across weeks</p>
            </header>



            {/* Week Comparison Header Cards */}
            <div className={styles.kpiGrid4}>
                {weekCards.map((w, idx) => (
                    <div key={w.key} style={{
                        background: idx === 3 ? 'linear-gradient(135deg, var(--color-primary), var(--color-secondary))' : 'var(--color-card)',
                        color: idx === 3 ? 'white' : 'inherit',
                        borderRadius: 'var(--radius-lg)',
                        padding: 'var(--spacing-3)',
                        border: idx === 3 ? 'none' : '1px solid var(--color-border)',
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)', marginBottom: 'var(--spacing-2)' }}>
                            <Calendar size={16} />
                            <span style={{ fontWeight: 600, fontSize: 'var(--font-size-sm)' }}>{w.label}</span>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--spacing-3)' }}>
                            <div>
                                <div style={{ fontSize: '10px', opacity: 0.7 }}>Value</div>
                                <div style={{ fontSize: 'var(--font-size-lg)', fontWeight: 700 }}>{formatCurrency(w.value, { compact: true })}</div>
                                {w.valueGrowth !== 0 && (
                                    <div style={{
                                        fontSize: '10px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 2,
                                        color: idx === 3 ? 'rgba(255,255,255,0.8)' : (w.valueGrowth > 0 ? 'var(--color-success)' : 'var(--color-danger)')
                                    }}>
                                        {w.valueGrowth > 0 ? <ArrowUpRight size={10} /> : <ArrowDownRight size={10} />}
                                        {formatPercentage(Math.abs(w.valueGrowth))}
                                    </div>
                                )}
                            </div>
                            <div>
                                <div style={{ fontSize: '10px', opacity: 0.7 }}>Volume</div>
                                <div style={{ fontSize: 'var(--font-size-lg)', fontWeight: 700 }}>{formatNumber(w.volume)}</div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Best/Worst Day Cards */}
            <div className={styles.kpiGrid2} style={{ marginTop: 'var(--spacing-4)' }}>
                <KPICard
                    title="Best Day"
                    value={bestDay?.day || 'N/A'}
                    trend={null}
                    icon={Award}
                    color="success"
                />
                <KPICard
                    title="Lowest Day"
                    value={worstDay?.day || 'N/A'}
                    trend={null}
                    icon={AlertTriangle}
                    color="warning"
                />
            </div>

            {/* Charts Row 1 */}
            <section className={styles.section}>
                <h2 className={styles.sectionTitle}>
                    <BarChart3 size={20} className={styles.sectionIcon} />
                    Weekly Performance
                </h2>

                <div className={styles.chartsGrid}>
                    <ChartCard title="Weekly Value" icon={DollarSign} height={200}>
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={valueData}>
                                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                                <XAxis dataKey="week" tick={{ fontSize: 11 }} />
                                <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}K`} />
                                <Tooltip formatter={(v) => formatCurrency(v)} contentStyle={{ backgroundColor: 'var(--color-card)', border: '1px solid var(--color-border)', borderRadius: '8px' }} />
                                <Legend />
                                <Bar dataKey="Value" fill="#4A90E2" radius={[4, 4, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </ChartCard>

                    <ChartCard title="Weekly Volume" icon={Package} height={200}>
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={volumeData}>
                                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                                <XAxis dataKey="week" tick={{ fontSize: 11 }} />
                                <YAxis tick={{ fontSize: 11 }} />
                                <Tooltip contentStyle={{ backgroundColor: 'var(--color-card)', border: '1px solid var(--color-border)', borderRadius: '8px' }} />
                                <Legend />
                                <Bar dataKey="Volume" fill="#50C878" radius={[4, 4, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </ChartCard>
                </div>
            </section>

            {/* Daily Breakdown + Heatmap */}
            <section className={styles.section}>
                <div className={styles.chartsGrid}>
                    <ChartCard title="Daily Breakdown (Stacked)" icon={Layers} height={280}>
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={dailyBreakdownData}>
                                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                                <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                                <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}K`} />
                                <Tooltip formatter={(v) => formatCurrency(v)} contentStyle={{ backgroundColor: 'var(--color-card)', border: '1px solid var(--color-border)', borderRadius: '8px' }} />
                                <Legend />
                                {weekLabels.map((w, idx) => (
                                    <Area
                                        key={w.key}
                                        type="monotone"
                                        dataKey={w.short}
                                        stackId="1"
                                        stroke={['#4A90E2', '#50C878', '#FFB347', '#7B68EE'][idx]}
                                        fill={['#4A90E2', '#50C878', '#FFB347', '#7B68EE'][idx]}
                                        fillOpacity={0.6}
                                    />
                                ))}
                            </AreaChart>
                        </ResponsiveContainer>
                    </ChartCard>

                    <ChartCard title="Day-of-Week Revenue Heatmap (₹ '000)" icon={Grid3X3} height={400}>
                        <Heatmap
                            data={heatmapData}
                            rows={weekLabels.map(w => w.short)}
                            columns={DAYS}
                            valueKey="value"
                        />
                    </ChartCard>
                </div>
            </section>

            {/* Category + WoW Growth */}
            <section className={styles.section}>
                <div className={styles.chartsGrid}>
                    <ChartCard title="Category Weekly Revenue" icon={Layers} height={220}>
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={categoryWeeklyData}>
                                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                                <XAxis dataKey="week" tick={{ fontSize: 11 }} />
                                <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}K`} />
                                <Tooltip formatter={(v) => formatCurrency(v)} contentStyle={{ backgroundColor: 'var(--color-card)', border: '1px solid var(--color-border)', borderRadius: '8px' }} />
                                <Legend />
                                {categories.slice(0, 4).map(cat => (
                                    <Bar key={cat.id} dataKey={cat.shortName} fill={cat.color} />
                                ))}
                            </BarChart>
                        </ResponsiveContainer>
                    </ChartCard>

                    <ChartCard title="Week-over-Week Growth" icon={TrendingUp} height={220}>
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={wowGrowthData}>
                                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                                <XAxis dataKey="week" tick={{ fontSize: 11 }} />
                                <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${v}%`} />
                                <Tooltip formatter={(v) => formatPercentage(v)} contentStyle={{ backgroundColor: 'var(--color-card)', border: '1px solid var(--color-border)', borderRadius: '8px' }} />
                                <Legend />
                                <Line type="monotone" dataKey="Value Growth" stroke="#4A90E2" strokeWidth={2} dot={{ fill: '#4A90E2' }} />
                                <Line type="monotone" dataKey="Volume Growth" stroke="#50C878" strokeWidth={2} dot={{ fill: '#50C878' }} />
                            </LineChart>
                        </ResponsiveContainer>
                    </ChartCard>
                </div>
            </section>
        </div>
    );
}

export default WeeklyAnalysis;
